import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import axios from 'axios';
import { LockKeyhole, Settings, ShieldCheck } from 'lucide-react';
import PageHeader from '../components/PageHeader';

export default function SettingsPage() {
  const { user, updateUser } = useAuth();
  const [email, setEmail] = useState(user?.email || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Synchroniser l'email avec l'utilisateur du contexte
  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
    }
  }, [user?.email]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess('');
    setError('');
    if (newPassword && newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }
    setLoading(true);
    try {
      const dataToSend: any = {
        email,
        currentPassword
      };
      
      // N'ajouter le nouveau mot de passe que s'il n'est pas vide
      if (newPassword) {
        dataToSend.newPassword = newPassword;
      }
      
      await axios.put('/api/users/me', dataToSend);
      setSuccess('Modifications enregistrées !');
      
      // Mettre à jour le contexte utilisateur avec le nouvel email
      updateUser({ email });
      
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la modification.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-8">
      <PageHeader eyebrow="Compte adhérent" title="Mes paramètres" description="Mettez à jour votre adresse email et sécurisez l'accès à votre espace personnel." icon={<Settings />} />
      <div className="grid gap-6 lg:grid-cols-[.75fr_1.25fr]">
        <aside className="card h-fit">
          <span className="page-header-icon"><ShieldCheck /></span>
          <h2 className="mt-6 text-2xl font-bold">Votre compte reste privé.</h2>
          <p className="mt-3 leading-7 text-gray-600">Votre mot de passe actuel est toujours demandé avant une modification. Choisissez un nouveau mot de passe unique et difficile à deviner.</p>
        </aside>
        <section className="card">
          <div className="mb-6 flex items-center gap-3"><LockKeyhole className="h-5 w-5 text-primary-600" /><h2 className="text-xl font-bold">Identifiants de connexion</h2></div>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div><label className="mb-2 block text-sm font-medium text-gray-700">Email</label><input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="input-field w-full min-w-0 px-4 py-3" /></div>
            <div><label className="mb-2 block text-sm font-medium text-gray-700">Mot de passe actuel</label><input type="password" required value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="input-field w-full min-w-0 px-4 py-3" /></div>
            <div className="grid gap-5 md:grid-cols-2">
              <div><label className="mb-2 block text-sm font-medium text-gray-700">Nouveau mot de passe</label><input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="input-field w-full min-w-0 px-4 py-3" placeholder="Facultatif" /></div>
              <div><label className="mb-2 block text-sm font-medium text-gray-700">Confirmation</label><input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="input-field w-full min-w-0 px-4 py-3" placeholder="Répétez le mot de passe" /></div>
            </div>
            <button type="submit" className="btn-primary w-full" disabled={loading}>{loading ? 'Enregistrement...' : 'Enregistrer les modifications'}</button>
            {success && <div className="rounded-xl bg-green-50 p-3 text-center text-green-700">{success}</div>}
            {error && <div className="rounded-xl bg-red-50 p-3 text-center text-red-700">{error}</div>}
          </form>
        </section>
      </div>
    </div>
  );
}
